import { useEffect, useRef, useState } from 'react';
import useManagedContent from './useManagedContent';
import useFetchGiftCardsListing from './useFetchGiftCardsListing';
import {
  clearFacet,
  getSelectedFacets,
  getUpdatedFacets
} from '../../../helpers';

const useGiftCardListing = ({
  listingEndpoint,
  managedContentEndpoint,
  itemId
}) => {
  //State Hooks
  const [initialized, setInitialized] = useState(false);
  const [searchHappened, setSearchHappened] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hastNextPage, setHasNextPage] = useState(false);
  const [loadMore, setLoadMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [facets, setFacets] = useState([]);
  const [goal, setGoal] = useState();
  const [giftCards, setGiftCards] = useState([]);
  const [facetChange, setFacetChange] = useState(0);
  const giftCardListingElement = useRef(null);
  const prevScrollY = useRef(window.scrollY);
  // Axios Hooks
  const managedContentRequest = useManagedContent(managedContentEndpoint);
  const [
    { data, loading: loadingGiftCards, error: errorGiftCards },
    fetchGiftCards
  ] = useFetchGiftCardsListing(listingEndpoint, {
    id: itemId,
    pageNumber
  });

  useEffect(() => {
    fetchGiftCards();
    window.addEventListener('scroll', handleInfiniteScroll);
    return () => window.removeEventListener('scroll', handleInfiniteScroll);
  }, []);

  useEffect(() => {
    if (!data) return;
    setInitialized(true);
    const isNewPage = data.pageNumber > pageNumber;
    setGoal(data.goal);
    if (data.giftCards)
      setGiftCards(
        isNewPage ? giftCards.concat(data.giftCards) : data.giftCards
      );
    if (data.facets) setFacets(data.facets);
    setPageNumber(data.pageNumber);
    setLoadMore(false);
    setHasNextPage(data.itemsPerPage * pageNumber < data.itemsTotal);
  }, [data]);

  useEffect(() => {
    if (!loadMore || !hastNextPage) return;
    refetchGiftCards(false, false, pageNumber + 1);
  }, [loadMore]);

  useEffect(() => {
    setPageNumber(1);
  }, [searchQuery, facetChange]);

  const refetchGiftCards = (_facets, _searchQuery, _pageNumber) => {
    if (loadingGiftCards) return;
    setSearchHappened(true);

    fetchGiftCards({
      ...getSelectedFacets(_facets || facets),
      id: itemId,
      pageNumber: _pageNumber || pageNumber,
      searchQuery: _searchQuery || searchQuery
    });
  };

  const handleInfiniteScroll = () => {
    const { pageYOffset } = window;
    if (!giftCardListingElement.current || prevScrollY.current > pageYOffset)
      return (prevScrollY.current = pageYOffset);
    const { offsetHeight } = giftCardListingElement.current;
    if (pageYOffset >= offsetHeight) {
      setLoadMore(true);
    }
    prevScrollY.current = pageYOffset;
  };

  const handleFacetChange = (name, value) => {
    const updatedFacets = getUpdatedFacets(facets, name, value);
    setFacets(updatedFacets);
    setPageNumber(1);
    refetchGiftCards(updatedFacets, false, 1);
  };

  const handleFacetClear = name => {
    const updatedFacets = clearFacet(facets, name);
    setFacets(updatedFacets);
    setPageNumber(1);
    refetchGiftCards(updatedFacets, false, 1);
  };

  return {
    searchQuery,
    setSearchQuery,
    managedContentRequest,
    goal,
    giftCards,
    facets,
    refetchGiftCards,
    handleFacetChange,
    handleFacetClear,
    loadingGiftCards,
    giftCardListingElement,
    errorGiftCards,
    initialized,
    searchHappened
  };
};

export default useGiftCardListing;
